import { shuffle } from "lodash";
import { Dispatch, UnknownAction } from "redux";

import { createPollSessionHelper } from "api/api_helpers";
import { ForumAPI } from "contexts/forum_api_context";
import {
  PaneContentType,
  PaneType,
  PollSessionStatusType,
  PollSessionType,
  PollType,
  ResourceType,
  TimelinePaneType,
  TimelinePaneTypeType,
} from "shared/types";
import { RoleType, UserType } from "shared/types/user";
import { getEmptyPaneIndex, getPaneIndexForUser } from "shared/utils/stage";
import {
  leastTalkTimeSubsort,
  pickUserToReplace,
  pickUsersToAddToAndRemoveFromStage,
} from "shared/utils/user_picker";

export const EMPTY_PANE: PaneType = { content_id: undefined, content_type: PaneContentType.Empty };

function _findPresentStarredUserId(users: UserType[]) {
  const presentGuestInstructors = users.filter(
    (user) => user.role === RoleType.GuestInstructor && user.is_present,
  );
  const presentTeacher = users.find((user) => user.role === RoleType.Teacher && user.is_present);
  const presentAssistants = users.filter(
    (user) => user.role === RoleType.Assistant && user.is_present,
  );
  return presentGuestInstructors[0]?.id || presentTeacher?.id || presentAssistants[0]?.id;
}

// Other users = non-starred users without the canParticipateInClass capability, i.e., TAs + backup instructors
function _findPresentOtherUserId(users: UserType[]) {
  const presentOtherUsers = users.filter(
    (user) => [RoleType.BackupInstructor, RoleType.TA].includes(user.role) && user.is_present,
  );
  return presentOtherUsers[0]?.id;
}

export function findPollByTitle(polls: PollType[], title: string | undefined) {
  return polls.find((poll) => poll.title.toLowerCase() === title?.toLowerCase());
}

export function findNthPollSession(
  pollSessions: PollSessionType[],
  poll: PollType,
  nth: number = 0,
) {
  const sortedPollSessions = pollSessions
    .filter((session) => session.poll_id === poll.id)
    .sort((a, b) => a.started_at - b.started_at);
  return sortedPollSessions[nth];
}

function _findResourceByTitle(resources: ResourceType[], title: string | undefined) {
  return resources.find((resource) => resource.title.toLowerCase() === title?.toLowerCase());
}

// Prioritizes polls, resources, and pinned/speaking users over non-speaking users
// Empty panes are filled with users when possible
export function buildStagePanesForLayoutChange(
  currentPanes: PaneType[],
  targetNumberOfPanes: number,
  presentVisibleUsers: UserType[],
  eligibleUserIds: string[], // for a breakout, this is present assigned or visiting users
  isAlwaysSubscribedToUserIds: string[],
  canParticipateInClassUserIds: string[],
) {
  const [usersToAddToStage, usersToRemoveFromStage] = pickUsersToAddToAndRemoveFromStage(
    presentVisibleUsers,
    eligibleUserIds,
    isAlwaysSubscribedToUserIds,
    canParticipateInClassUserIds,
    currentPanes,
  );
  const userIdsToAddToStage = usersToAddToStage.map((user) => user.id);
  const userIdsToRemoveFromStage = usersToRemoveFromStage.map((user) => user.id);
  const newPanes = [];

  // In an odd-numbered layout, the large pane (the first one) is reserved (empty)
  // EXCEPTION: In 1-up layout, if there's a poll or resource, it takes the single spot
  const hasPriorityContent = currentPanes.some(
    (pane) =>
      pane.content_type === PaneContentType.PollSession ||
      pane.content_type === PaneContentType.Resource,
  );

  // Only skip empty pane for 1-up layouts when priority content exists
  const shouldSkipEmptyForPriorityContent = targetNumberOfPanes === 1 && hasPriorityContent;

  if (
    targetNumberOfPanes % 2 === 1 &&
    !shouldSkipEmptyForPriorityContent && // Only affects 1-up layouts
    (currentPanes[0].content_type === PaneContentType.User ||
      currentPanes[0].content_type === PaneContentType.Empty)
  ) {
    newPanes.push(EMPTY_PANE);
  }

  // Preserve all non-empty panes for now
  for (let i = 0; i < currentPanes.length; i += 1) {
    if (currentPanes[i].content_type !== PaneContentType.Empty) {
      newPanes.push(currentPanes[i]);
    }
  }

  // If we have fewer panes than we need, add users to the stage
  // Fill remaining spots with empty panes if needed
  while (newPanes.length < targetNumberOfPanes) {
    if (userIdsToAddToStage.length > 0) {
      newPanes.push({
        content_id: userIdsToAddToStage.shift(),
        content_type: PaneContentType.User,
      });
    } else {
      newPanes.push(EMPTY_PANE);
    }
  }

  // If we have more panes than we need, remove non-speaking, non-pinned users from the stage
  // If there are no more users to remove, remove panes from the end
  while (newPanes.length > targetNumberOfPanes) {
    if (userIdsToRemoveFromStage.length > 0) {
      const userId = userIdsToRemoveFromStage.shift();
      const index = newPanes.findIndex(
        (pane) => pane.content_type === PaneContentType.User && pane.content_id === userId,
      );
      // Prevent splice(-1, 1) from removing last element when user not found
      if (index !== -1) {
        newPanes.splice(index, 1);
      }
    } else {
      newPanes.pop();
    }
  }

  return newPanes;
}

// Populate stage with users in userIdsToAdd.
// Do not remove users if they are speaking.
// Do not remove users if they are in a pinned pane.
// Do not remove starred users.
// Do not replace empty pane with a user if it is the first pane in an odd-numbered layout.
export function buildStagePanesWithUsers(
  currentPanes: PaneType[],
  userIdsToAdd: string[],
  allUsers: UserType[],
  isAlwaysSubscribedToUserIds: string[],
): [PaneType[], boolean] {
  const newPanes = [];
  let changed = false;
  // Filter out starred users who are already on stage as they will not be removed
  const userIdsOnStage = currentPanes
    .filter((pane) => pane.content_type === PaneContentType.User)
    .map((pane) => pane.content_id);
  const availableUserIds = userIdsToAdd.filter(
    (userId) => !(isAlwaysSubscribedToUserIds.includes(userId) && userIdsOnStage.includes(userId)),
  );

  for (let i = 0; i < currentPanes.length; i += 1) {
    let isPaneAvailable =
      currentPanes[i].content_type === PaneContentType.Empty &&
      !(currentPanes.length % 2 === 1 && i === 0);

    let isReplacingSameUserPane = false;
    if (!isPaneAvailable && currentPanes[i].content_type === PaneContentType.User) {
      const paneUserId = currentPanes[i].content_id;
      const paneUser = allUsers.find((user) => user.id === paneUserId);
      isReplacingSameUserPane = paneUserId === availableUserIds[0];
      isPaneAvailable =
        !!paneUserId &&
        !isAlwaysSubscribedToUserIds.includes(paneUserId) &&
        !paneUser?.is_speaking &&
        !currentPanes[i].is_pinned;
    }

    if (isPaneAvailable) {
      if (availableUserIds.length > 0) {
        newPanes.push({
          content_id: availableUserIds.shift(),
          content_type: PaneContentType.User,
        });
      } else {
        newPanes.push(EMPTY_PANE);
      }
      if (!isReplacingSameUserPane) {
        changed = true;
      }
    } else {
      newPanes.push(currentPanes[i]);
    }
  }

  return [newPanes, changed];
}

// Feature up to 8 present students who are assigned to the given breakout/preclass group on stage.
// If the resource needs to be displayed, then display up to 6 students.
export function buildStagePanesForFeaturingGroup(userIdsToFeature: string[], resourceId?: string) {
  const newPanes = [];

  if (resourceId) {
    newPanes.push({
      content_id: resourceId,
      content_type: PaneContentType.Resource,
    });
  }

  const maxStudents = resourceId ? 6 : 8;
  const availableUserIds = userIdsToFeature.slice(0, maxStudents);

  availableUserIds.forEach((userId) => {
    newPanes.push({
      content_id: userId,
      content_type: PaneContentType.User,
    });
  });

  if (resourceId && newPanes.length > 2 && newPanes.length % 2 === 0) {
    newPanes.push(EMPTY_PANE);
  }

  return newPanes;
}

export async function buildStagePanesFromTimelinePanes(
  timelinePanes: TimelinePaneType[],
  users: UserType[],
  participantBarUserIds: string[],
  polls: PollType[],
  pollSessions: PollSessionType[],
  resources: ResourceType[],
  isInPersonClass: boolean,
  dispatch: Dispatch<UnknownAction>,
  forumAPI: ForumAPI,
) {
  const stagePanes: PaneType[] = [];
  const eligibleStudentUsers = users.filter(
    (user) => user.role === "student" && user.is_present && participantBarUserIds.includes(user.id),
  );
  let eligibleStudentUsersSortedByTalkTime = leastTalkTimeSubsort(eligibleStudentUsers);
  let randomizedEligibleStudentUsers: UserType[] = shuffle(eligibleStudentUsers);

  for (const timelinePane of timelinePanes) {
    switch (timelinePane.type) {
      case TimelinePaneTypeType.Poll: {
        const poll = findPollByTitle(polls, timelinePane.name);
        if (poll) {
          const inProgressPollSession = pollSessions.find(
            (pollSession) =>
              pollSession.poll_id === poll.id &&
              pollSession.status === PollSessionStatusType.InProgress,
          );
          if (inProgressPollSession) {
            stagePanes.push({
              content_id: inProgressPollSession.id,
              content_type: PaneContentType.PollSession,
            });
            break;
          }
          // We could collect the promises, put placeholders in the panes, then await and replace the
          // placeholders in order to make this process parallel, which would increase complexity, however.
          // eslint-disable-next-line no-await-in-loop
          const pollSession = await createPollSessionHelper(poll.id, dispatch, forumAPI).catch(
            () => {
              return null;
            },
          );
          if (pollSession) {
            stagePanes.push({
              content_id: pollSession.id,
              content_type: PaneContentType.PollSession,
            });
            break;
          }
        }
        window.flightLogger.error(`Poll ${timelinePane.name} not found`);
        stagePanes.push(EMPTY_PANE);
        break;
      }

      case TimelinePaneTypeType.PollResult: {
        const poll = findPollByTitle(polls, timelinePane.name);
        const nth = timelinePane.instance_back;
        const pollSession = poll && findNthPollSession(pollSessions, poll, nth);
        if (!pollSession) {
          stagePanes.push(EMPTY_PANE);
          break;
        }

        stagePanes.push({
          content_id: pollSession.id,
          content_type: PaneContentType.PollSession,
        });
        break;
      }

      case TimelinePaneTypeType.QuietStudent:
        if (!isInPersonClass) {
          const quietStudent = eligibleStudentUsersSortedByTalkTime.shift();
          randomizedEligibleStudentUsers = randomizedEligibleStudentUsers.filter(
            (user: UserType) => user.id !== quietStudent?.id,
          );
          if (quietStudent) {
            stagePanes.push({
              content_id: quietStudent.id,
              content_type: PaneContentType.User,
            });
          } else {
            stagePanes.push(EMPTY_PANE);
          }
        }
        break;

      case TimelinePaneTypeType.RandomStudent: {
        if (!isInPersonClass) {
          const randomStudent = randomizedEligibleStudentUsers.shift();
          eligibleStudentUsersSortedByTalkTime = eligibleStudentUsersSortedByTalkTime.filter(
            (user) => user.id !== randomStudent?.id,
          );
          if (randomStudent) {
            stagePanes.push({
              content_id: randomStudent.id,
              content_type: PaneContentType.User,
            });
          } else {
            stagePanes.push(EMPTY_PANE);
          }
        }
        break;
      }

      case TimelinePaneTypeType.Resource: {
        let resource = _findResourceByTitle(resources, timelinePane.name);
        // Forum Workbooks will have a template prefix attached to them if the workbook is used in a breakout group.
        if (!resource) {
          resource = _findResourceByTitle(resources, `[template] ${timelinePane.name}`);
        }
        if (resource) {
          stagePanes.push({
            content_id: resource.id,
            content_type: PaneContentType.Resource,
          });
        } else {
          window.flightLogger.error(`Resource ${timelinePane.name} not found`);
          stagePanes.push(EMPTY_PANE);
        }
        break;
      }

      case TimelinePaneTypeType.Teacher:
        if (!isInPersonClass) {
          const starredUserId = _findPresentStarredUserId(users);
          // If there are no available starred users, feature a random student
          let randomStudent: UserType | undefined;
          if (!starredUserId) {
            randomStudent = randomizedEligibleStudentUsers.shift();
            eligibleStudentUsersSortedByTalkTime = eligibleStudentUsersSortedByTalkTime.filter(
              (user) => user.id !== randomStudent?.id,
            );
          }
          // If there are no available starred users or students, feature a TA or backup instructor
          let otherUserId;
          if (!starredUserId && !randomStudent) {
            otherUserId = _findPresentOtherUserId(users);
          }
          const userId = starredUserId || randomStudent?.id || otherUserId;
          if (userId) {
            stagePanes.push({
              content_id: userId,
              content_type: PaneContentType.User,
            });
          } else {
            stagePanes.push(EMPTY_PANE);
          }
        }
        break;

      default:
        window.flightLogger.warn(`Unknown timeline pane type ${timelinePane.type}`);
    }
  }

  return stagePanes;
}

export function findBestPaneIndex(
  presentVisibleUsers: UserType[],
  isAlwaysSubscribedToUserIds: string[],
  currentPanes: PaneType[],
  participantBarUserIds: string[] = [],
) {
  // First try finding an empty pane
  const emptyPaneIndex = getEmptyPaneIndex(currentPanes);
  if (emptyPaneIndex !== -1) {
    return emptyPaneIndex;
  }

  // If there are no empty panes, try replacing another user
  const userToReplace = pickUserToReplace(
    presentVisibleUsers,
    isAlwaysSubscribedToUserIds,
    currentPanes,
    participantBarUserIds,
    true,
  );
  return userToReplace ? getPaneIndexForUser(currentPanes, userToReplace.id) : -1;
}

export function getVisiblePanes(
  panes: PaneType[],
  isInPerson: boolean,
  isPresentationView: boolean,
  isMobile: boolean,
  canControlClassroom: boolean,
  activeBreakout: boolean,
) {
  let visiblePanes = panes;

  if (isInPerson) {
    visiblePanes = panes.flatMap((pane) =>
      pane.content_type !== PaneContentType.User ? pane : [],
    );
  }

  if (isPresentationView) {
    visiblePanes = panes.filter(
      (pane) => !pane.hidden_in_presentation_view && pane.content_type !== PaneContentType.Empty,
    );
  }

  // Students in in-person classes on mobile screen sizes can only see content that they can interact with.
  if (isInPerson && isMobile && !canControlClassroom && !activeBreakout) {
    // TODO Filter SLE and whiteboards by whether they are student-editable
    const editablePaneContentTypes = [PaneContentType.PollSession];
    visiblePanes = panes.filter((pane) => editablePaneContentTypes.includes(pane.content_type));
  }

  return visiblePanes;
}
